import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  GitBranch,
  Palette,
  Code2,
  ShieldCheck,
  Rocket,
  Headphones,
} from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";

// ============================================================
// PROCESS ICONS
// ============================================================

const processIcons = [
  Search,
  GitBranch,
  Palette,
  Code2,
  ShieldCheck,
  Rocket,
  Headphones,
];

// ============================================================
// ANIMATIONS
// ============================================================

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const headerItemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    x: -35,
  },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ============================================================
// PROCESS SECTION
// ============================================================

const ProcessSection = () => {
  const { process } = softwareDevelopmentData;

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]

        py-8
        min-[375px]:py-9
        sm:py-10
        md:py-12
        lg:py-16
        xl:py-20
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-24
          top-16
          h-40
          w-40
          rounded-full
          bg-orange-500/[0.05]
          blur-[75px]

          min-[375px]:h-48
          min-[375px]:w-48
          min-[375px]:blur-[85px]

          sm:-left-32
          sm:top-24
          sm:h-56
          sm:w-56
          sm:blur-[100px]

          md:h-72
          md:w-72
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-24
          bottom-12
          h-44
          w-44
          rounded-full
          bg-orange-500/[0.04]
          blur-[80px]

          min-[375px]:h-52
          min-[375px]:w-52
          min-[375px]:blur-[90px]

          sm:-right-32
          sm:bottom-20
          sm:h-64
          sm:w-64
          sm:blur-[110px]

          md:h-80
          md:w-80
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl

          px-3
          min-[375px]:px-4
          sm:px-6
          md:px-7
          lg:px-8
          xl:px-10
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.2,
          }}
          variants={containerVariants}
          className="
            mx-auto
            mb-7
            w-full
            max-w-3xl
            text-center

            min-[375px]:mb-8
            sm:mb-10
            md:mb-11
            lg:mb-12
          "
        >
          {/* =================================================
              LABEL
          ================================================== */}

          <motion.span
            variants={headerItemVariants}
            className="
              inline-flex
              max-w-full
              items-center
              justify-center
              overflow-hidden
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]

              px-2.5
              py-1.5

              text-[8px]
              font-semibold
              uppercase
              tracking-[1.2px]
              text-[var(--brand-light)]

              min-[375px]:px-3
              min-[375px]:text-[9px]
              min-[375px]:tracking-[1.5px]

              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
          >
            <span className="truncate">
              Our Process
            </span>
          </motion.span>

          {/* =================================================
              HEADING
          ================================================== */}

          <motion.h2
            variants={headerItemVariants}
            className="
              mx-auto
              mt-3
              max-w-[95%]
              break-words

              text-[24px]
              font-bold
              leading-[1.15]
              tracking-tight
              text-[var(--text-primary)]

              min-[375px]:mt-4
              min-[375px]:text-[26px]

              sm:mt-5
              sm:max-w-2xl
              sm:text-3xl

              md:text-4xl

              lg:text-5xl
            "
          >
            A Smarter Way to{" "}
            <span className="text-[var(--brand-primary)]">
              Build Software
            </span>
          </motion.h2>

          {/* =================================================
              DESCRIPTION
          ================================================== */}

          <motion.p
            variants={headerItemVariants}
            className="
              mx-auto
              mt-3
              w-full
              max-w-[95%]
              break-words

              text-[10.5px]
              leading-[1.7]
              text-[var(--text-secondary)]

              min-[375px]:text-[11px]

              sm:mt-4
              sm:max-w-2xl
              sm:text-sm
              sm:leading-6

              md:text-base
              md:leading-7
            "
          >
            From the first conversation to launch and beyond, our structured
            development process keeps your project focused, transparent,
            scalable, and aligned with your business objectives.
          </motion.p>
        </motion.div>

        {/* =================================================
            TIMELINE
        ================================================== */}

        <div className="relative w-full">
          {/* =================================================
              DESKTOP TIMELINE LINE
          ================================================== */}

          <div
            className="
              pointer-events-none
              absolute
              left-6
              top-7
              hidden
              h-[calc(100%-3.5rem)]
              w-[2px]
              bg-gradient-to-b
              from-orange-500
              via-orange-500/50
              to-transparent

              lg:block
              xl:left-7
            "
          />

          {/* =================================================
              PROCESS ITEMS
          ================================================== */}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.08,
            }}
            variants={containerVariants}
            className="
              w-full

              space-y-4
              min-[375px]:space-y-5
              sm:space-y-6

              md:space-y-6

              lg:space-y-7
            "
          >
            {process.map((item, index) => {
              const Icon = processIcons[index] || Code2;

              return (
                <motion.div
                  key={item.step}
                  variants={itemVariants}
                  className="
                    group
                    relative
                    flex
                    w-full
                    min-w-0

                    flex-col
                    gap-2.5

                    min-[375px]:gap-3

                    sm:gap-3.5

                    lg:flex-row
                    lg:items-center
                    lg:gap-7
                  "
                >
                  {/* =================================================
                      ICON
                  ================================================== */}

                  <motion.div
                    whileHover={{
                      scale: 1.06,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                    className="
                      relative
                      z-10
                      flex
                      shrink-0
                      items-center
                      justify-center
                      self-start

                      h-10
                      w-10

                      rounded-full
                      border
                      border-orange-500/30
                      bg-[var(--card-bg)]
                      text-[var(--brand-primary)]

                      shadow-[0_0_0_3px_var(--bg-primary)]

                      transition-all
                      duration-300

                      hover:border-orange-500/60
                      hover:bg-[var(--brand-primary)]
                      hover:text-white

                      min-[375px]:h-11
                      min-[375px]:w-11
                      min-[375px]:shadow-[0_0_0_4px_var(--bg-primary)]

                      sm:h-14
                      sm:w-14

                      md:h-14
                      md:w-14

                      lg:h-14
                      lg:w-14
                    "
                  >
                    <Icon
                      size={18}
                      strokeWidth={1.8}
                      className="
                        transition-transform
                        duration-300
                        group-hover:scale-110

                        min-[375px]:h-5
                        min-[375px]:w-5

                        sm:h-[22px]
                        sm:w-[22px]
                      "
                    />
                  </motion.div>

                  {/* =================================================
                      CONTENT CARD
                  ================================================== */}

                  <div
                    className="
                      min-w-0
                      w-full
                      flex-1
                      overflow-hidden

                      rounded-xl
                      border
                      border-[var(--border-primary)]
                      bg-[var(--card-bg-secondary)]

                      px-3.5
                      py-3.5

                      transition-all
                      duration-300

                      hover:-translate-y-1
                      hover:border-orange-500/30
                      hover:bg-[var(--card-bg)]

                      min-[375px]:rounded-2xl
                      min-[375px]:px-4
                      min-[375px]:py-4

                      sm:px-6
                      sm:py-6

                      md:px-6
                      md:py-5

                      lg:px-7
                      lg:py-6
                    "
                  >
                    {/* =================================================
                        STEP
                    ================================================== */}

                    <span
                      className="
                        block
                        max-w-full
                        break-words

                        text-[8px]
                        font-semibold
                        uppercase
                        tracking-[1.2px]
                        text-[var(--brand-light)]

                        min-[375px]:text-[9px]
                        min-[375px]:tracking-[1.5px]

                        sm:text-xs
                        sm:tracking-[2px]
                      "
                    >
                      {item.step}
                    </span>

                    {/* =================================================
                        TITLE
                    ================================================== */}

                    <h3
                      className="
                        mt-1.5
                        max-w-full
                        break-words

                        text-[15px]
                        font-bold
                        leading-snug
                        text-[var(--text-primary)]

                        transition-colors
                        duration-300

                        group-hover:text-[var(--brand-light)]

                        min-[375px]:text-base

                        sm:text-xl
                      "
                    >
                      {item.title}
                    </h3>

                    {/* =================================================
                        DESCRIPTION
                    ================================================== */}

                    <p
                      className="
                        mt-1.5
                        w-full
                        max-w-4xl
                        break-words

                        text-[10.5px]
                        leading-[1.7]
                        text-[var(--text-muted)]

                        transition-colors
                        duration-300

                        group-hover:text-[var(--text-secondary)]

                        min-[375px]:mt-2
                        min-[375px]:text-[11px]

                        sm:text-sm
                        sm:leading-6
                      "
                    >
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* =================================================
            PROCESS END MESSAGE
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="
            mx-auto
            mt-6
            flex
            w-full
            max-w-full
            flex-wrap
            items-center
            justify-center
            gap-1.5
            px-1
            text-center

            min-[375px]:gap-2

            sm:mt-9
            sm:gap-3
          "
        >
          {/* =================================================
              STARS
          ================================================== */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-0.5
            "
          >
            <span className="text-[11px] text-[var(--brand-primary)] sm:text-sm">
              ★
            </span>

            <span className="text-[11px] text-[var(--brand-primary)] sm:text-sm">
              ★
            </span>

            <span className="text-[11px] text-[var(--brand-primary)] sm:text-sm">
              ★
            </span>

            <span className="text-[11px] text-[var(--brand-primary)] sm:text-sm">
              ★
            </span>

            <span className="text-[11px] text-[var(--brand-primary)] sm:text-sm">
              ★
            </span>
          </div>

          {/* =================================================
              MESSAGE
          ================================================== */}

          <span
            className="
              max-w-[90%]
              break-words
              text-[10px]
              leading-5
              text-[var(--text-muted)]

              min-[375px]:text-[11px]

              sm:max-w-none
              sm:text-sm
            "
          >
            From idea to successful launch
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;